import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallBlockV1Service, serviceName } from '../service';
import { ListBlockedNumbersIO, ServiceQueries } from '../types';
import { useCallBlockV1Queries } from './use-call-block-v1-queries';
import { UseCallBlockV1QueryEndpointArgs, useCallBlockV1Query } from './use-call-block-v1-query';

/**
 * A hook that returns a query for the `ListBlockedNumbers` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListBlockedNumbersQuery = <E = unknown, D = ListBlockedNumbersIO['output']>(
  args: UseCallBlockV1QueryEndpointArgs<'ListBlockedNumbers', E, D>
) =>
  useCallBlockV1Query({
    endpointName: 'ListBlockedNumbers',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListBlockedNumbers` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListBlockedNumbersQueries = <E = unknown, D = ListBlockedNumbersIO['output']>(
  argsArr: UseCallBlockV1QueryEndpointArgs<'ListBlockedNumbers', E, D>[]
) =>
  useCallBlockV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListBlockedNumbers',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListBlockedNumbers` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListBlockedNumbers = () => {
  const queryClient = useQueryClient();

  const fetchListBlockedNumbers = async <E = unknown, D = ListBlockedNumbersIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallBlockV1QueryEndpointArgs<'ListBlockedNumbers', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListBlockedNumbersIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBlockedNumbers'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListBlockedNumbersIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBlockedNumbers'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListBlockedNumbers'>({
          serviceName,
          endpointName: 'ListBlockedNumbers',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallBlockV1Service.ListBlockedNumbers(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListBlockedNumbers;
};
